import { useCallback, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import queryClient from '@/queryClient'
import { useOperationPageTemplateAttributes } from '../../../OperationDocument/hooks/useOperationPageTemplateAttributes'
import { useDocumentState } from '@/state'
import { useBulkCreateTemplateAttributes } from '@/services/queries/template_attributes'
import { useDeleteTemplateAttribute } from '@/services/queries/template_attributes'
import {
  getDocumentPageQuery,
  QUERY_KEYS as DOCUMENT_PAGE_QUERY_KEYS,
} from '@/services/queries/document_pages'
import { useTimeTravelStore } from '@/lib/timeTravel/timeTravelStore'
import { updateDocumentPage } from '@/lib/api/client'
import type { TemplateAttribute } from '@/lib/api/client'

export const useApplyTemplateToOperation = ({
  themeTemplateAttributes,
  onSuccess,
}: {
  themeTemplateAttributes: TemplateAttribute[]
  onSuccess: () => void
}) => {
  const { mutateAsync: bulkCreateTemplateAttributes } =
    useBulkCreateTemplateAttributes({
      isTimeTraveling: false,
    })

  const { mutateAsync: deleteTemplateAttribute } = useDeleteTemplateAttribute({
    isTimeTraveling: false,
  })

  const currentDocumentPageId = useDocumentState(
    (state) => state.currentPageInView,
  )

  const operationPageTemplateAttributeData = useOperationPageTemplateAttributes(
    {
      documentPageId: currentDocumentPageId,
    },
  )

  const operationPageTemplateAttributes = useMemo(
    () =>
      Object.values(operationPageTemplateAttributeData).flatMap(
        (x) => x || ([] as TemplateAttribute[]),
      ),
    [operationPageTemplateAttributeData],
  )

  const resetTimeTravelStore = useTimeTravelStore(
    useShallow((state) => state.reset),
  )

  const applyThemesToOperation = useCallback(async () => {
    if (currentDocumentPageId) {
      const documentPage = await getDocumentPageQuery({
        documentPageId: currentDocumentPageId,
      })

      await updateDocumentPage(currentDocumentPageId, {
        name: documentPage.name,
        template_values: {
          ...(documentPage.template_values || {}),
          created_using_document_theme: true,
        },
        parent_id: documentPage.parent || null,
      })

      await queryClient.invalidateQueries({
        queryKey: [DOCUMENT_PAGE_QUERY_KEYS.DOCUMENT_PAGES, {}],
      })

      // 1. Create new template attributes using the theme

      const existingPlaceholders = new Set(
        operationPageTemplateAttributes
          .filter(
            (attr) =>
              !attr.template_values?.subPageId ||
              attr.template_values?.subPageId === documentPage.id,
          )
          .filter((attr) => {
            if (attr.data_type === 'note') {
              return attr.template_values?.content === '<p></p>'
            }
            if (attr.data_type === 'view') {
              return !attr.template_values?.viewId
            }
            if (attr.data_type === 'image') {
              return !attr.template_values?.imageId
            }

            return false
          })
          .map((attr) => attr.id),
      )

      // 1.1. Keep content from the current templates and add
      //      it to the theme template attributes.
      const usedTemplateAttributes = new Set()
      const richThemeTemplateAttributes = themeTemplateAttributes.map(
        (themeAttr) => {
          const newThemeAttr = structuredClone(
            themeAttr,
          ) as TemplateAttribute & {
            template_values: any
          }

          const existingPageAttr = operationPageTemplateAttributes.find(
            (attr) => {
              const conditions = [
                attr.document_page === documentPage.id,
                !usedTemplateAttributes.has(attr.id),
                !attr.template_values?.removed,
                attr.data_type === newThemeAttr.data_type,

                // avoid using existing placeholders
                !existingPlaceholders.has(attr.id),

                // avoid applying themes to sub pages
                !attr.template_values?.subPageId ||
                  attr.template_values?.subPageId === documentPage.id,
              ]

              if (attr.data_type !== 'view') {
                conditions.push(
                  attr.template_values?.type ===
                    newThemeAttr.template_values?.type,
                )
              }

              return conditions.every((x) => x)
            },
          )

          if (existingPageAttr) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const themeTemplateValues = structuredClone(
              newThemeAttr.template_values,
            ) as any

            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const pageTemplateValues = structuredClone(
              existingPageAttr.template_values,
            ) as any

            // Merge existing table columns with theme table columns
            if (existingPageAttr.data_type === 'table') {
              const seen = new Set()

              const isPartsTable =
                newThemeAttr.data_type === 'table' &&
                newThemeAttr.template_values?.type === 'parts-table'

              const isToolsTable =
                newThemeAttr.data_type === 'table' &&
                newThemeAttr.template_values?.type === 'tools-table'

              const combinedColumns = [
                ...(pageTemplateValues?.columns || []),
                ...(themeTemplateValues?.columns || []),
              ]
                .map((column) => {
                  // Custom columns include is document-page-id. We need to update
                  // the custom columns with the document-page-id that we're currently
                  // focused-in on within the app
                  for (let key in column) {
                    const val = column[key]
                    const isCustomColumn = key.includes('custom-column_')

                    const allowDeduplicating =
                      isPartsTable || isToolsTable ? isCustomColumn : true

                    // Prevent duplicating custom columns
                    const columnAlreadyExists = seen.has(val)
                    if (allowDeduplicating && columnAlreadyExists) {
                      delete column[key]
                      continue
                    }

                    const isForeignCustomColumn = !key.includes(
                      `custom-column_${documentPage.id}`,
                    )

                    // Update custom column with the correct document page id
                    if (isCustomColumn && isForeignCustomColumn) {
                      const randn = Math.floor(Math.random() * 1000)
                      column[`custom-column_${documentPage.id}_${randn}`] = val
                      delete column[key]
                    }

                    seen.add(val)
                  }
                  return column
                })
                .filter((column) => Object.keys(column || {}).length > 0)

              pageTemplateValues.columns = combinedColumns
            }

            newThemeAttr.template_values = {
              ...themeTemplateValues,
              ...pageTemplateValues,
            }

            const position = themeTemplateValues?.position
            const size = themeTemplateValues?.size

            delete newThemeAttr.template_values?.position
            delete newThemeAttr.template_values?.size

            if (position) {
              newThemeAttr.template_values.position = position
            }

            if (size) {
              newThemeAttr.template_values.size = size
            }

            usedTemplateAttributes.add(existingPageAttr.id)
          }

          delete newThemeAttr.template_values?.subPageId

          return newThemeAttr
        },
      )

      // 2. Now that theme template attributes have content, we'll
      //    add the attributes to the page
      await bulkCreateTemplateAttributes({
        documentPageId: currentDocumentPageId,
        payloads: richThemeTemplateAttributes,
      })

      // 3. Delete all of the operation template attributes

      //    !!NOTE: We're only going to delete `data_type` that have
      //            been used in the new theme (i.e. `usedTemplateAttributes`).
      //            We're going to keep the remain template attributes to ensure
      //            that no content is deleted

      for (const templateId of usedTemplateAttributes) {
        const t = operationPageTemplateAttributes.find(
          (x) => x.document_page === documentPage.id && x.id === templateId,
        )
        if (t && t.id && currentDocumentPageId) {
          await deleteTemplateAttribute({
            templateAttributeId: t.id,
            documentPageId: currentDocumentPageId,
          })
        }
      }

      for (const templateId of existingPlaceholders) {
        const t = operationPageTemplateAttributes.find(
          (x) => x.document_page === documentPage.id && x.id === templateId,
        )

        if (
          t &&
          t.id &&
          currentDocumentPageId &&
          !usedTemplateAttributes.has(t.id)
        ) {
          await deleteTemplateAttribute({
            templateAttributeId: t.id,
            documentPageId: currentDocumentPageId,
          })
        }
      }

      // Reset time-travel
      resetTimeTravelStore()
    }
  }, [
    bulkCreateTemplateAttributes,
    currentDocumentPageId,
    deleteTemplateAttribute,
    operationPageTemplateAttributes,
    resetTimeTravelStore,
    themeTemplateAttributes,
  ])

  return useMutation({
    mutationFn: applyThemesToOperation,
    onSettled: onSuccess,
  })
}
